import React from "react";
import KanbanCard from "./kanban-card";
import { Card, CardHeader, CardContent } from "../../components/ui/card";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useDroppable } from "@dnd-kit/core";
import { Plus } from "lucide-react";

// ✅ Define lifecycle stage labels
const STAGE_LABELS: Record<
  NonNullable<ClientType["lifecycleStage"]>,
  string
> = {
  prospect: "Prospect",
  initial_outreach: "Initial Outreach",
  benefits_analysis: "Benefits Analysis",
  proposal_sent: "Proposal Sent",
  customer: "Customer",
  lost: "Lost",
};

// Stage colors for visual differentiation
const STAGE_COLORS: Record<
  NonNullable<ClientType["lifecycleStage"]>,
  { bg: string; text: string; border: string }
> = {
  prospect: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    border: "border-blue-200",
  },
  initial_outreach: {
    bg: "bg-indigo-50",
    text: "text-indigo-700",
    border: "border-indigo-200",
  },
  benefits_analysis: {
    bg: "bg-violet-50",
    text: "text-violet-700",
    border: "border-violet-200",
  },
  proposal_sent: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    border: "border-amber-200",
  },
  customer: {
    bg: "bg-green-50",
    text: "text-green-700",
    border: "border-green-200",
  },
  lost: { bg: "bg-gray-50", text: "text-gray-700", border: "border-gray-200" },
};

interface KanbanColumnProps {
  stage: NonNullable<ClientType["lifecycleStage"]>;
  clients: ClientType[];
  className?: string;
}

const KanbanColumn: React.FC<KanbanColumnProps> = ({
  stage,
  clients,
  className,
}) => {
  // ✅ Enable dropping on empty columns
  const { setNodeRef, isOver, active } = useDroppable({ id: stage });

  const stageColor = STAGE_COLORS[stage];
  const clientCount = clients.length;
  const isBeingDroppedOn = isOver && active;

  return (
    <Card
      ref={setNodeRef}
      className={`w-1/8 min-w-[246px] bg-white rounded-md ${className} ${
        isBeingDroppedOn ? "ring-2 ring-primary ring-offset-2" : ""
      }`}
    >
      <CardHeader
        className={`${stageColor.bg} ${stageColor.text} p-4 rounded-t-md flex flex-row justify-between items-center`}
      >
        <div className="flex items-center">
          <h2 className="text-lg font-semibold capitalize">
            {STAGE_LABELS[stage]}
          </h2>
          <div
            className={`ml-2 px-2 py-0.5 text-xs font-medium rounded-full ${stageColor.bg} ${stageColor.text} border ${stageColor.border}`}
          >
            {clientCount}
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-2 space-y-3 min-h-[150px] flex flex-col">
        <SortableContext
          items={clients.map((client) => String(client.id))}
          strategy={verticalListSortingStrategy}
        >
          {clients.length > 0 ? (
            clients.map((client) => (
              <KanbanCard
                key={client.id}
                id={String(client.id)}
                client={client}
              />
            ))
          ) : (
            <div
              className={`
              h-24 flex flex-col items-center justify-center 
              border-2 border-dashed rounded-lg
              ${
                isBeingDroppedOn
                  ? "border-primary/50 bg-primary/5"
                  : `${stageColor.border} bg-gray-50/50`
              }
              transition-colors duration-200
            `}
            >
              <Plus className={`h-5 w-5 ${stageColor.text} opacity-40 mb-1`} />
              <p className="text-xs text-gray-500">Drop client here</p>
            </div>
          )}
        </SortableContext>
      </CardContent>
    </Card>
  );
};

export default KanbanColumn;
